/**
 * Debug page for testing the CountryLocator component with Pacific island nations.
 * Route: #countrylocatordebug
 */

import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { CountryLocator } from '@/components/CountryLocator';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { CountryCode } from '@/data/countries/types';

// Test countries - focus on Pacific/dateline edge cases
const testCountries: {
  code: CountryCode;
  name: string;
  description: string;
}[] = [
  {
    code: 'FJ',
    name: 'Fiji',
    description: 'Crosses the dateline (177°E to -178°W)',
  },
  { code: 'MH', name: 'Marshall Islands', description: 'Far Pacific (~171°E)' },
  { code: 'KI', name: 'Kiribati', description: 'Spans across dateline' },
  { code: 'NZ', name: 'New Zealand', description: 'Near dateline (~174°E)' },
  { code: 'RU', name: 'Russia', description: 'Crosses dateline (huge span)' },
  { code: 'AU', name: 'Australia', description: 'Standard large country' },
  { code: 'FR', name: 'France', description: 'Standard European (control)' },
  { code: 'US', name: 'United States', description: 'Large with territories' },
];

interface CountryLocatorDebugProps {
  onBack: () => void;
}

export function CountryLocatorDebug({ onBack }: CountryLocatorDebugProps) {
  const [selectedCountry, setSelectedCountry] = useState(testCountries[0]);

  return (
    <div className="h-dvh w-full flex flex-col bg-stone-100">
      {/* Header */}
      <header className="shrink-0 bg-white border-b p-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-display text-xl font-bold">
              CountryLocator Debug
            </h1>
            <p className="text-sm text-muted-foreground">
              Test Pacific/dateline rendering
            </p>
          </div>
        </div>
      </header>

      {/* Country selector */}
      <div className="shrink-0 bg-white border-b p-4">
        <div className="flex flex-wrap gap-2">
          {testCountries.map((country) => (
            <Button
              key={country.code}
              variant={
                selectedCountry.code === country.code ? 'default' : 'outline'
              }
              size="sm"
              onClick={() => setSelectedCountry(country)}
            >
              {country.name}
            </Button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {selectedCountry.description}
        </p>
      </div>

      {/* Map view */}
      <main className="flex-1 min-h-0 p-4">
        <Card className="h-full overflow-hidden">
          <CardHeader className="py-3">
            <CardTitle className="text-sm">
              {selectedCountry.name} ({selectedCountry.code})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 h-[calc(100%-3.5rem)]">
            <div
              className="w-full h-full"
              data-testid="country-locator-container"
            >
              <CountryLocator
                countryName={selectedCountry.name}
                countryCode={selectedCountry.code}
              />
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default CountryLocatorDebug;
